import Entity, { TEntity, TState } from '@/plugins/podolib/services/entities/Entity';
import { TStation } from '@/plugins/podolib/services/entities/EntityStations';
import { TAcquisition } from '@/plugins/podolib/services/entities/EntityPatients';
import { GetterTree } from 'vuex';

export enum ESide {
  Left = 'left',
  Right = 'right',
}

export type TSoleFirmware = {
  type: string;
  min: number;
  build: number;
  arch: string;
  maj: number;
};

export type TSoleMeta = {
  battery: number;
  mac: string;
  sn: string;
  firmware: TSoleFirmware;
  footSize: number;
};

export type TSoles = {
  _id: string;
  ref: string;
  abbr: string;
  updatable: boolean;
  calibrated: boolean;
  checked: false;
} & {
  readonly [P in ESide]: TSoleMeta;
};

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class EntitySoles extends Entity<TSoles> {
  name: string = 'soles';

  public get mutations() {
    return {
      ...super.mutations,
      ...{
        UPDATE(state: any, id: string) {
          state.list.map((x: any) => {
            if (x._id === id) x.updatable = false;
          });
        },
        RESET_CHECKED(state: any) {
          state.list.map((x: any) => {
            x.checked = false;
          });
        },
      },
    };
  }

  public get getters(): GetterTree<TState<TEntity<TSoles>>, any> {
    return {
      ...super.getters,
      ...{
        getCheckedSoles: (state) => {
          return state.list.filter((x) => x.checked);
        },
        getUpdatableSoles: (state) => {
          return state.list.filter((x) => x.updatable);
        },
        getCalibratableSoles: (state) => {
          return state.list.filter((x) => !x.calibrated);
        },
      },
    };
  }

  extendListeners() {
    this.Vue.prototype.$pdc.repository.socket.session
      .on('soles assigned', () => {
        this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}:assigned`);
        return this.sync({ mergeWithLastUsedQuery: true });
      })
      .on('soles unassigned', () => {
        this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}:unassigned`);
        return this.sync({ mergeWithLastUsedQuery: true });
      });
  }

  getBatteries(): Promise<{ soles: string[] }> {
    this.store.commit(`pdc/entities/${this.name}/RESET_CHECKED`);

    function onBattery(this: EntitySoles, sole: any) {
      this.store.commit(`pdc/entities/${this.name}/PATCH_ONE`, { ...sole, checked: true });

      this.Vue.prototype.$pdc.bus.emitDebug('entities:soles:patched', { ...sole, checked: true });
    }

    this.Vue.prototype.$pdc.repository.socket.session.on(`${this.name} patched`, onBattery.bind(this));
    /**
     * After 5s, station stop to send battery levels
     * @deprecated
     */
    setTimeout(() => {
      this.Vue.prototype.$pdc.repository.socket.session?.removeListener(`${this.name} patched`, onBattery);
      const checkedSoles = this.Vue.prototype.$pdc.entities!.soles.storedEntity.list.filter((x: any) => x.checked);
      for (const sole of checkedSoles) {
        // Analytics
        this.Vue.prototype.$pdc.analytics?.actions.trackSuccess('Insole Refreshed', {
          abbr: sole.abbr,
          batteries: {
            left: sole.left.battery,
            right: sole.right.battery,
          },
        });
      }
    }, 6000);

    return this.Vue.prototype.$pdc.repository.emitCommand('get battery', {
      command: 'get battery',
      data: {
        stationsIds: this.Vue.prototype.$pdc.entities!.stations.storedEntity.list.map(
          (station: TEntity<TStation>) => station._id,
        ),
        solesIds: this.Vue.prototype.$pdc.entities!.soles.storedEntity.list.map((sole: TEntity<TSoles>) => sole._id),
      },
    });
  }
}
